'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HousingBadge } from '@eliadmin/core/src/components/housing/housing-badge';
import { CgRuler } from 'react-icons/cg';
import { HiOutlineTicket, HiOutlineUserGroup } from 'react-icons/hi';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import type { HousingProperty } from '@eliadmin/core/src/api/housing/housing-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ChooseHouse({ properties }: { properties: HousingProperty[] }) {
  const { t } = useTranslations();
  const [activeIndex, setActiveIndex] = useState(0);
  const active = properties[activeIndex];

  if (!active) return null;

  return (
    <section
      className={cn('custom-section', 'section-padding-lg-top', 'section-padding-xl-bottom')}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-[30px] md:flex-row md:items-center md:justify-between',
        )}
      >
        <div className="relative flex aspect-square max-h-[600px] w-full items-end justify-end overflow-hidden rounded-[24px] p-3 sm:p-4 md:max-w-[580px] lg:p-6">
          <div className="relative z-10 flex w-full flex-wrap items-start justify-start gap-1 sm:gap-2">
            <HousingBadge
              before={<CgRuler size={16} className="text-secondary" />}
              name={`${active.area} m²`}
            />
            <HousingBadge
              before={<HiOutlineTicket size={16} className="text-secondary" />}
              name={`${active.price_per_night} €/noc`}
            />
            <HousingBadge
              before={<HiOutlineUserGroup size={16} className="text-secondary" />}
              name={`${active.capacity_total} hostí`}
            />
          </div>
          <Image
            src={active.image?.url ?? '/images/horec.webp'}
            alt={resolveImageAlt(active?.image, active?.name)}
            fill
            className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          />
        </div>

        <div className="flex w-full flex-col items-start justify-start gap-6 md:max-w-[540px]">
          <div className="grid w-full grid-cols-2 items-center justify-start gap-2 rounded-full bg-[#F2EEE7] p-1 md:flex md:w-fit">
            {properties.map((property, index) => (
              <button
                key={property.id}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={cn(
                  'flex h-[42px] items-center justify-center rounded-full px-6 text-base font-bold tracking-tight transition-colors sm:h-[45px]',
                  index === activeIndex
                    ? 'bg-secondary text-white'
                    : 'text-secondary hover:bg-secondary/10',
                )}
              >
                {property.name}
              </button>
            ))}
          </div>

          <Paragraph
            innerHtml={active.short_description}
            className={cn('w-full', 'ea-generic-formatting')}
          />

          <div className="flex flex-col items-center justify-start gap-2 sm:flex-row">
            <Button asChild className="w-full sm:w-fit">
              <Link href={'/rezervacia'}>
                <ButtonIcon alt={t('text538')} url="/images/icons/date-primary.svg" />
                {t('text538')}
              </Link>
            </Button>
            <Button asChild variant={'outline'} className="w-full sm:w-fit">
              <Link href={active.slug}>{t('text792')}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
